import React, { useState, useEffect } from "react";
import {
  Container, Typography, Box, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Button, Chip, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField,
  CircularProgress, Pagination, InputAdornment, FormControl,
  Select, MenuItem
} from "@mui/material";
import {
  Search,
  Refresh,
  FilterList,
  Visibility
} from "@mui/icons-material";
import { api } from "../context/AuthContext";

function SuperAdminTickets() {
  const [queries, setQueries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [page, setPage] = useState(1);
  const [replyDialogOpen, setReplyDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedQuery, setSelectedQuery] = useState(null);
  const [replyMessage, setReplyMessage] = useState("");
  const [submittingReply, setSubmittingReply] = useState(false);

  useEffect(() => { fetchAllQueries(); }, []);

  const fetchAllQueries = async () => {
    setLoading(true);
    try {
      const response = await api.get("/support-queries");
      if (response.data && Array.isArray(response.data)) {
        setQueries(response.data.map((q, i) => ({
          ...q,
          id: q.id || q._id || `query-${i}`,
          displayId: q.id ? (q.id.length > 8 ? q.id.substring(0, 8) : q.id) : `TKT-${i+1}`,
          date: new Date(q.created_at || q.createdAt || Date.now()).toLocaleString(),
          updatedDate: q.updated_at ? new Date(q.updated_at).toLocaleString() : 'N/A'
        })));
      } else {
        setQueries([]);
      }
    } catch (error) { 
      console.error("Error fetching tickets:", error);
      alert("Failed to load support tickets. Check console for details.");
    } finally { setLoading(false); }
  };

  const filteredQueries = queries.filter(q => {
    const matchesSearch = (q.user_email?.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (q.email?.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (q.message?.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesFilter = statusFilter === 'All' || 
      (q.status || 'pending').toLowerCase() === statusFilter.toLowerCase();

    return matchesSearch && matchesFilter;
  });

  const statusOptions = Array.from(new Set([
    'All',
    'Pending',
    'Resolved',
    ...queries.map(q => {
      const s = q.status || 'Pending';
      return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
    })
  ]));

  const paginatedQueries = filteredQueries.slice((page - 1) * 10, page * 10);
  const totalPages = Math.ceil(filteredQueries.length / 10);

  const handleSendReply = async () => {
    if (!replyMessage.trim() || !selectedQuery) return;

    // FINAL FIX: Check every possible field name for the email
    const recipientEmail = selectedQuery.user_email || selectedQuery.email || selectedQuery.candidate_email;

    if (!recipientEmail) {
      alert("Error: No email address found for this ticket. Please check your database.");
      return;
    }

    setSubmittingReply(true);
    try {
      await api.post(`/support-queries/${selectedQuery.id}/reply`, { replyMessage, email: recipientEmail });
      alert("Reply sent successfully!");
      setReplyDialogOpen(false);
      fetchAllQueries();
    } catch (error) { 
      console.error(error);
      alert(error.response?.data?.message || "Error occurred"); 
    } finally { setSubmittingReply(false); }
  };

  if (loading) return <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}><CircularProgress /></Box>;

  return (
      <Container sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Box>
            {/*<Typography variant="h2" sx={{ color: "#1E3A8A", fontWeight: "bold" }}>*/}
            <Typography
                variant="h4"
                sx={{
                  color: "#1E3A8A",
                  fontWeight: "bold",
                  fontSize: { xs: "1.5rem", md: "2rem" }
                }}
            >
              Support Tickets
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Review and respond to support requests from users.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <TextField
              size="small"
              placeholder="Search by Phone Number or Email"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(1);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
              sx={{ width: 300 }}
            />
            <FormControl size="small">
              <Select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(1);
                }}
                displayEmpty
                renderValue={() => (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#1E3A8A', fontWeight: 'bold' }}>
                    <FilterList sx={{ fontSize: 20 }} />
                    Filters
                  </Box>
                )}
                sx={{
                  borderRadius: 0,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#1E3A8A',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#1E40AF',
                  },
                  '& .MuiSelect-select': {
                    display: 'flex',
                    alignItems: 'center',
                  }
                }}
              >
                <MenuItem disabled sx={{ fontWeight: 'bold', color: '#1E3A8A', borderBottom: '1px solid #eee', justifyContent: 'center', opacity: '1 !important' }}>
                  Filters
                </MenuItem>
                {statusOptions.map((option) => (
                  <MenuItem key={option} value={option}>{option}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button 
              variant="outlined" 
              startIcon={<Refresh />} 
              onClick={fetchAllQueries}
              size="small"
              sx={{ height: '40px' }}
            >
              Refresh
            </Button>
          </Box>
        </Box>

        <TableContainer component={Paper} sx={{ borderRadius: 0, boxShadow: 3 }}>
          <Table>
            <TableHead sx={{ bgcolor: '#f5f5f5' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Subject</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>From</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedQueries.map((query) => (
                <TableRow key={query.id} hover>
                  <TableCell sx={{ fontWeight: 'medium' }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#1E3A8A' }}>
                      {query.subject || "No Subject"}
                    </Typography>
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        display: '-webkit-box',
                        WebkitLineClamp: 1,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        color: 'text.secondary'
                      }}
                    >
                      {query.message}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {query.user_email || query.email || query.user_name || "Guest"}
                  </TableCell>
                  <TableCell>{query.date}</TableCell>
                  <TableCell>
                    <Chip 
                      label={(query.status || "PENDING").toUpperCase()}
                      size="small"
                      variant="outlined"
                      color={
                        query.status?.toLowerCase() === 'resolved' ? 'success' : 'warning'
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <Button
                          size="small"
                          variant="outlined"
                          startIcon={<Visibility />}
                          onClick={() => {
                            setSelectedQuery(query);
                            setViewDialogOpen(true);
                          }}
                      >
                        View
                      </Button>

                      <Button
                          size="small"
                          variant={query.status?.toLowerCase() === "resolved" ? "outlined" : "contained"}
                          onClick={() => {
                            setSelectedQuery(query);
                            setReplyDialogOpen(true);
                          }}
                          disabled={query.status?.toLowerCase() === "resolved"}
                      >
                        {query.status?.toLowerCase() === "resolved" ? "Resolved" : "Reply"}
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
              {filteredQueries.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No support tickets found.</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mt: 3,
              flexWrap: "wrap",
              gap: 2,
            }}
        >
          <Typography variant="body2" color="text.secondary">
            {paginatedQueries.length > 0 ? (page - 1) * 10 + 1 : 0}–
            {Math.min(page * 10, filteredQueries.length)} of{" "}
            {filteredQueries.length} records
          </Typography>
          <Pagination
              count={totalPages}
              page={page}
              onChange={(e, value) => setPage(value)}
              shape="rounded"
              color="primary"
              sx={{
                "& .MuiPaginationItem-root.Mui-selected": {
                  backgroundColor: "#1E3A8A",
                  "&:hover": {
                    backgroundColor: "#1E40AF",
                  },
                },
              }}
          />
        </Box>
        <Dialog
            open={viewDialogOpen}
            onClose={() => setViewDialogOpen(false)}
            fullWidth
            maxWidth="md"
        >
          <DialogTitle
              sx={{
                bgcolor: "#1E40AF",
                color: "white",
                fontWeight: "bold"
              }}
          >
            Support Ticket Details
          </DialogTitle>

          <DialogContent dividers>
            <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: "bold",
                  mb: 1
                }}
            >
              Subject
            </Typography>

            <Typography variant="h6" sx={{ mb: 3 }}>
              {selectedQuery?.subject || "No Subject"}
            </Typography>

            <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: "bold",
                  mb: 2
                }}
            >
              Message
            </Typography>

            <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  bgcolor: "#fafafa"
                }}
            >
              <Typography>
                {selectedQuery?.message || "No Message Available"}
              </Typography>
            </Paper>
          </DialogContent>

          <DialogActions>
            <Button onClick={() => setViewDialogOpen(false)}>
              Close
            </Button>

            {selectedQuery?.status?.toLowerCase() !== "resolved" && (
                <Button
                    variant="contained"
                    sx={{ bgcolor: "#1E40AF" }}
                    onClick={() => {
                      setViewDialogOpen(false);
                      setReplyDialogOpen(true);
                    }}
                >
                  Reply
                </Button>
            )}
          </DialogActions>
        </Dialog>
        <Dialog open={replyDialogOpen} onClose={() => setReplyDialogOpen(false)} fullWidth>
          <DialogTitle sx={{ bgcolor: '#1E40AF', color: 'white' }}>Reply to Ticket</DialogTitle>
          <DialogContent dividers>
            <TextField fullWidth multiline rows={4} value={replyMessage} onChange={(e) => setReplyMessage(e.target.value)} variant="outlined" margin="normal" />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setReplyDialogOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleSendReply} disabled={!replyMessage.trim() || submittingReply} sx={{ bgcolor: "#1E40AF" }}>
              {submittingReply ? "Sending..." : "Send Email Reply"}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
  );
}
export default SuperAdminTickets;
